import React, { useState, useRef, useEffect, useCallback } from 'react';

const MIN_WIDTH = 260;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 450;

function renderMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/```[\w]*\n?([\s\S]*?)```/g, (_, code) =>
      `<pre style="background:#2d2d2d;color:#f8f8f2;padding:8px 10px;border-radius:5px;overflow-x:auto;font-size:11.5px;margin:4px 0;white-space:pre-wrap;"><code>${code.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</code></pre>`)
    .replace(/`([^`]+)`/g, '<code style="background:#e8e2d8;padding:1px 4px;border-radius:3px;font-size:12px;">$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^### (.+)$/gm, '<div style="font-weight:700;font-size:12.5px;margin:8px 0 2px;">$1</div>')
    .replace(/^## (.+)$/gm, '<div style="font-weight:700;font-size:13px;margin:10px 0 3px;">$1</div>')
    .replace(/^# (.+)$/gm, '<div style="font-weight:700;font-size:13.5px;margin:10px 0 3px;">$1</div>')
    .replace(/^[*\-] (.+)$/gm, '<div style="padding-left:12px;margin:1px 0;">• $1</div>')
    .replace(/^\d+\. (.+)$/gm, (_, item, offset, str) => {
      const num = str.slice(0, offset).split('\n').filter(l => /^\d+\./.test(l)).length + 1;
      return `<div style="padding-left:12px;margin:1px 0;">${num}. ${item}</div>`;
    })
    .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid #CCC5B9;margin:6px 0;"/>')
    .replace(/\n/g, '<br/>');
}

export default function AiChat({ canvases, activeCanvasId, currentNodes, cadData, open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [panelWidth, setPanelWidth] = useState(DEFAULT_WIDTH);
  const bottomRef = useRef(null);
  const abortRef = useRef(null);
  const resizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);

  // 최신 값을 ref로 유지 (stale closure 방지)
  const canvasesRef = useRef(canvases);
  const currentNodesRef = useRef(currentNodes);
  const activeCanvasIdRef = useRef(activeCanvasId);
  const cadDataRef = useRef(cadData);
  useEffect(() => { canvasesRef.current = canvases; }, [canvases]);
  useEffect(() => { currentNodesRef.current = currentNodes; }, [currentNodes]);
  useEffect(() => { activeCanvasIdRef.current = activeCanvasId; }, [activeCanvasId]);
  useEffect(() => { cadDataRef.current = cadData; }, [cadData]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const getBoardContext = useCallback(() => {
    const cvs = canvasesRef.current;
    const aid = activeCanvasIdRef.current;
    const cnodes = currentNodesRef.current;
    const cad = cadDataRef.current;

    // cadData가 있으면 전체 품번 데이터를 CSV 형식으로 전달
    if (cad && cad.length > 0) {
      const header = 'part_cd,품명,브랜드,시즌,도메인,아이템,소재,컬러,가격,핏';
      const rows = cad.slice(0, 100).map(d => [
        d.part_cd || '',
        d.description || d.prdt_nm || '',
        d.brd_cd || '',
        d.sesn || '',
        d.domain || '',
        d.item || '',
        d.fabric || d.fab_info || '',
        (d.color || d.color_list || '').replace(/,/g, '/'),
        d.pre_tag || d.tag || '',
        d.fit || d.fit_info || '',
      ].join(',')).join('\n');

      const sheetInfo = cvs ? cvs.map(canvas => {
        const rawNodes = canvas.id === aid ? (cnodes || canvas.nodes || []) : (canvas.nodes || []);
        const placed = rawNodes.filter(n => n.type?.toLowerCase() === 'stylecard' && n.data?.part_cd).map(n => n.data.part_cd);
        return placed.length > 0 ? `[${canvas.name || canvas.id}] ${placed.join(', ')}` : null;
      }).filter(Boolean).join('\n') : '';

      return `전체 스타일 데이터 (총 ${cad.length}개, 상위 100개 표시):\n${header}\n${rows}${sheetInfo ? `\n\n시트별 배치:\n${sheetInfo}` : ''}`;
    }

    // cadData 없으면 캔버스 노드에서 수집
    if (!cvs || cvs.length === 0) return null;
    const activeCanvas = cvs.find(c => c.id === aid) || cvs[0];
    const sheetSummaries = cvs.map(canvas => {
      const rawNodes = canvas.id === aid ? (cnodes || canvas.nodes || []) : (canvas.nodes || []);
      const cards = rawNodes.filter(n => n.type?.toLowerCase() === 'stylecard' && n.data?.part_cd);
      if (cards.length === 0) return null;
      const cardLines = cards.slice(0, 50).map(n => {
        const d = n.data;
        const parts = [
          `품번:${d.part_cd}`,
          d.description ? `품명:${d.description}` : null,
          d.brd_cd ? `브랜드:${d.brd_cd}` : null,
          d.sesn ? `시즌:${d.sesn}` : null,
          d.domain ? `도메인:${d.domain}` : null,
          d.item ? `아이템:${d.item}` : null,
          d.fabric ? `소재:${d.fabric}` : null,
          d.color ? `컬러:${d.color}` : null,
          d.price ? `가격:${d.price}` : null,
          d.fit ? `핏:${d.fit}` : null,
        ].filter(Boolean).join(', ');
        return `  - ${parts}`;
      }).join('\n');
      return `[시트: ${canvas.name || canvas.id}] (${cards.length}개)\n${cardLines}`;
    }).filter(Boolean).join('\n\n');

    if (!sheetSummaries) return null;
    const activeName = activeCanvas?.name || activeCanvas?.id || '';
    return `보드 구성: ${cvs.length}개 시트, 현재 시트: ${activeName}\n\n${sheetSummaries}`;
  }, []);

  const initializedRef = useRef(false);

  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    resizingRef.current = true;
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = panelWidth;

    const onMouseMove = (moveEvent) => {
      if (!resizingRef.current) return;
      const delta = resizeStartXRef.current - moveEvent.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeStartWidthRef.current + delta));
      setPanelWidth(newWidth);
    };

    const onMouseUp = () => {
      resizingRef.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [panelWidth]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const res = await fetch('/api/ai-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: newMessages, boardContext: getBoardContext() }),
        signal: controller.signal,
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(l => l.startsWith('data: '));
        for (const line of lines) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.text) {
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  ...updated[updated.length - 1],
                  content: updated[updated.length - 1].content + data.text,
                };
                return updated;
              });
            } else if (data.error) {
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { ...updated[updated.length - 1], content: `오류: ${data.error}`, isError: true };
                return updated;
              });
            }
          } catch {}
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...updated[updated.length - 1], content: '오류가 발생했습니다.' };
          return updated;
        });
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{
      width: open ? panelWidth : 0,
      minWidth: open ? panelWidth : 0,
      height: '100%',
      background: '#FAF9F7',
      borderLeft: open ? '1px solid #CCC5B9' : 'none',
      display: 'flex',
      flexDirection: 'row',
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      {/* 리사이즈 핸들 */}
      {open && (
        <div
          onMouseDown={handleResizeMouseDown}
          style={{
            width: 2,
            cursor: 'col-resize',
            background: 'transparent',
            flexShrink: 0,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#CCC5B9'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        />
      )}

      {/* 패널 본문 */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* 헤더 */}
        <div style={{
          padding: '60px 16px 12px 16px',
          background: '#504A40',
          color: '#F4F3F0',
          fontWeight: 600,
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          minHeight: 46,
          whiteSpace: 'nowrap',
        }}>
          <span>AI Assistance</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => { setMessages([]); initializedRef.current = false; }}
              style={{ background: 'none', border: 'none', color: 'rgba(244,243,240,0.6)', cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              대화 초기화
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', color: 'rgba(244,243,240,0.7)',
                cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px',
                flexShrink: 0, display: 'flex', alignItems: 'center',
              }}
              title="닫기"
            >
              ✕
            </button>
          </div>
        </div>

        {/* 메시지 영역 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
              <div style={{
                padding: '8px 12px',
                borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                background: msg.role === 'user' ? '#777166' : msg.isError ? '#fff1f2' : '#EDE8E0',
                color: msg.role === 'user' ? '#F4F3F0' : msg.isError ? '#e11d48' : '#3D3530',
                fontSize: 13, lineHeight: 1.6, wordBreak: 'break-word',
                whiteSpace: msg.role === 'user' ? 'pre-wrap' : 'normal',
              }}>
                {msg.role === 'assistant' ? (
                  msg.content
                    ? <span dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                    : (loading && i === messages.length - 1 ? <span style={{ color: '#A19A8E' }}>▋</span> : null)
                ) : msg.content}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* 입력 영역 */}
        <div style={{ padding: '10px 12px', borderTop: '1px solid #D4CEC7', display: 'flex', gap: 8, alignItems: 'flex-end', flexShrink: 0, background: '#FAF9F7' }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="메시지를 입력하세요..."
            rows={1}
            style={{
              flex: 1, resize: 'none', border: '1px solid #CCC5B9', borderRadius: 8,
              padding: '8px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit',
              maxHeight: 80, overflowY: 'auto', background: '#fff', color: '#3D3530',
            }}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            style={{
              width: 36, height: 36, borderRadius: 8,
              background: input.trim() && !loading ? '#504A40' : '#CCC5B9',
              border: 'none', cursor: input.trim() && !loading ? 'pointer' : 'default',
              color: '#F4F3F0', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            ↑
          </button>
        </div>
      </div>
    </div>
  );
}
